import React, { createContext, useContext, useMemo, useState } from 'react';
import Api from 'talent_search/lib/api';
import type {
  Paths,
  SavedSearchGroup,
  SavedSearchItem,
} from 'talent_search/shared/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import type { RediscoveryProps } from 'talent_matching/types';

export interface RediscoveryContextValue {
  apiClient: Api;
  paths: Paths;
  milestones: Item[];
  currencies: Item[];
  savedSearchOptions: SavedSearchGroup[];
  unfilteredCount: number;
  userDateFormat: string;
  isActive: boolean;
  savedFilterSet: SavedSearchItem | null;
  setSavedFilterSet: (ss: SavedSearchItem | null) => void;
}

const RediscoveryContext = createContext<RediscoveryContextValue | undefined>(
  undefined
);

export const useRediscoveryContext = () => {
  const context = useContext(RediscoveryContext);
  if (!context) {
    throw new Error(
      'useRediscoveryContext must be used within a RediscoveryUserContextProvider'
    );
  }
  return context;
};

type RediscoveryUserContextProviderProps = {
  rediscoveryProps: RediscoveryProps;
  isActive?: boolean;
  children: React.ReactNode;
};

export const RediscoveryUserContextProvider = ({
  rediscoveryProps,
  isActive = true,
  children,
}: RediscoveryUserContextProviderProps) => {
  const [savedFilterSet, setSavedFilterSet] = useState<SavedSearchItem | null>(
    null
  );

  const apiClient = useMemo(() => {
    return new Api(rediscoveryProps.paths);
  }, [rediscoveryProps?.paths]);

  const contextValue: RediscoveryContextValue = useMemo(
    () => ({
      apiClient,
      paths: rediscoveryProps.paths,
      milestones: rediscoveryProps.milestones,
      currencies: rediscoveryProps.currencies,
      savedSearchOptions: rediscoveryProps.savedSearchOptions ?? [],
      unfilteredCount: rediscoveryProps.unfilteredCount,
      userDateFormat: rediscoveryProps.userDateFormat,
      isActive,
      savedFilterSet,
      setSavedFilterSet,
    }),
    [rediscoveryProps, isActive, apiClient, savedFilterSet]
  );

  return (
    <RediscoveryContext.Provider value={contextValue}>
      {children}
    </RediscoveryContext.Provider>
  );
};
